// src/components/communications/EmailManagement.js
import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Select, MenuItem,
  FormControl, InputLabel, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Checkbox, Chip, IconButton, Tooltip,
  CircularProgress, Snackbar
} from '@mui/material';
import { 
  Send as SendIcon, 
  Add as AddIcon, 
  Email as EmailIcon,
  FilterList as FilterIcon 
} from '@mui/icons-material';
import { communicationAPI, academicAPI } from '../../services/api';

const EmailManagement = () => {
  const [formData, setFormData] = useState({
    class_stream_id: '',
    message_type: 'due_reminder',
    custom_message: '',
    student_ids: []
  });
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [classStreams, setClassStreams] = useState([]);
  const [showMainDialog, setShowMainDialog] = useState(false);
  const [showStudentSelector, setShowStudentSelector] = useState(false);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [filterLoading, setFilterLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [sentEmails, setSentEmails] = useState([]);

  useEffect(() => {
    fetchClassStreams();
  }, []);

  const fetchClassStreams = async () => {
    try {
      const response = await academicAPI.getClassStreams();
      setClassStreams(response.data);
    } catch (error) {
      console.error('Error fetching class streams:', error);
    } finally {
      setInitialLoading(false);
    }
  };

  const fetchFilteredStudents = async () => {
    setFilterLoading(true);
    try {
      const response = await communicationAPI.getFilteredStudents({
        filter_type: formData.message_type,
        class_stream_id: formData.class_stream_id || undefined
      });
      setFilteredStudents(response.data.students);
      setSelectedStudents(response.data.students.map(s => s.id));
      setShowStudentSelector(true);
    } catch (error) {
      setSnackbar({ 
        open: true, 
        message: 'Error fetching students: ' + (error.response?.data?.message || 'Please try again'),
        severity: 'error'
      });
    } finally {
      setFilterLoading(false);
    }
  };

  const handleStudentToggle = (studentId) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSelectAll = () => {
    setSelectedStudents(filteredStudents.map(s => s.id));
  };

  const handleDeselectAll = () => {
    setSelectedStudents([]);
  };

  const handleConfirmSelection = () => {
    setFormData(prev => ({ ...prev, student_ids: selectedStudents }));
    setShowStudentSelector(false);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await communicationAPI.sendBulkReminders(formData);
      setSnackbar({ 
        open: true, 
        message: response.data.message || 'Bulk reminders sent successfully!',
        severity: 'success'
      });
      
      // Add to sent emails list
      setSentEmails(prev => [{
        id: Date.now(),
        message_type: formData.message_type,
        class_stream: formData.class_stream_id ? 
          classStreams.find(cs => cs.id === formData.class_stream_id)?.display_name : 'All Classes',
        recipients: formData.student_ids.length,
        sent_at: new Date().toLocaleString()
      }, ...prev]);

      setShowMainDialog(false);
      setFormData({
        class_stream_id: '',
        message_type: 'due_reminder',
        custom_message: '',
        student_ids: []
      });
      setSelectedStudents([]);
    } catch (error) {
      setSnackbar({ 
        open: true, 
        message: 'Error sending bulk reminders: ' + (error.response?.data?.message || 'Please try again'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  if (initialLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box mb={3} display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={2}>
          <EmailIcon color="primary" sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h4" fontWeight={700}>
              Email Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Send fee reminders to students with outstanding balances
            </Typography>
          </Box>
        </Box>
      </Box>

      <Card elevation={3}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight={600}>
              Sent Email History
            </Typography>
            <Tooltip title="Send Bulk Email">
              <IconButton 
                color="primary" 
                onClick={() => setShowMainDialog(true)}
                sx={{ 
                  bgcolor: 'primary.main', 
                  color: 'white',
                  '&:hover': { bgcolor: 'primary.dark' }
                }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <TableContainer component={Paper} sx={{ borderRadius: 2, maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Message Type</TableCell>
                  <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Class Stream</TableCell>
                  <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Recipients</TableCell>
                  <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Sent At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sentEmails.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 8, color: 'text.secondary' }}>
                      No emails sent yet. Click the + icon to send bulk email reminders.
                    </TableCell>
                  </TableRow>
                ) : (
                  sentEmails.map((email) => (
                    <TableRow key={email.id} hover>
                      <TableCell>
                        <Chip 
                          label={email.message_type === 'due_reminder' ? 'Due Reminder' : 'Overdue Notice'}
                          color={email.message_type === 'due_reminder' ? 'primary' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{email.class_stream}</TableCell>
                      <TableCell>{email.recipients}</TableCell>
                      <TableCell>{email.sent_at}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Main Email Form Dialog */}
      <Dialog open={showMainDialog} onClose={() => setShowMainDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Send Bulk Fee Reminders</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Class Stream (Optional)</InputLabel>
              <Select
                name="class_stream_id"
                value={formData.class_stream_id}
                label="Class Stream (Optional)"
                onChange={handleInputChange}
              >
                <MenuItem value="">All Classes</MenuItem>
                {classStreams.map(cs => (
                  <MenuItem key={cs.id} value={cs.id}>{cs.display_name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Message Type</InputLabel>
              <Select
                name="message_type"
                value={formData.message_type}
                label="Message Type"
                onChange={handleInputChange}
              >
                <MenuItem value="due_reminder">Due Reminder</MenuItem>
                <MenuItem value="overdue_notice">Overdue Notice</MenuItem>
              </Select>
            </FormControl>

            <Box mt={2} mb={2}>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={fetchFilteredStudents}
                disabled={filterLoading}
                fullWidth
              >
                {filterLoading ? 'Loading...' : 'Filter & Select Students'}
              </Button>
              {formData.student_ids.length > 0 && (
                <Box mt={1} display="flex" justifyContent="center">
                  <Chip 
                    label={`${formData.student_ids.length} students selected`} 
                    color="primary" 
                  />
                </Box>
              )}
            </Box>
            
            <TextField
              fullWidth
              name="custom_message"
              label="Custom Message (Optional)"
              multiline
              rows={4}
              value={formData.custom_message}
              onChange={handleInputChange}
              placeholder="Add any additional message..."
              margin="normal"
            />

            <Alert severity="info" sx={{ mt: 2 }}>
              Only students with outstanding fees and valid guardian emails will receive reminders.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowMainDialog(false)} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={loading || formData.student_ids.length === 0}
            startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
          >
            {loading ? 'Sending...' : 'Send Reminders'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Student Selection Dialog */}
      <Dialog open={showStudentSelector} onClose={() => setShowStudentSelector(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Select Students for {formData.message_type === 'due_reminder' ? 'Due Reminders' : 'Overdue Notices'}
        </DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <Button onClick={handleSelectAll} sx={{ mr: 1 }}>Select All</Button>
            <Button onClick={handleDeselectAll}>Deselect All</Button>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {selectedStudents.length} of {filteredStudents.length} students selected
            </Typography>
          </Box>
          
          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">Select</TableCell>
                  <TableCell>Student</TableCell>
                  <TableCell>Class</TableCell>
                  <TableCell>Guardian</TableCell>
                  <TableCell>Balance</TableCell>
                  <TableCell>Contact</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredStudents.map((student) => (
                  <TableRow key={student.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedStudents.includes(student.id)}
                        onChange={() => handleStudentToggle(student.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {student.full_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {student.registration_no}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{student.class_stream_name}</TableCell>
                    <TableCell>{student.guardian_name}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color={student.balance > 0 ? 'error' : 'success'}>
                        UGX {student.balance?.toLocaleString() || 0}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        {student.has_email && <Chip label="Email" size="small" color="success" sx={{ mr: 0.5 }} />}
                        {student.has_phone && <Chip label="Phone" size="small" color="info" />}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowStudentSelector(false)}>Cancel</Button>
          <Button onClick={handleConfirmSelection} variant="contained">
            Confirm Selection ({selectedStudents.length})
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EmailManagement;